import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase_setup/firebase";
import {
  Button,
  Card,
  Checkbox,
  Label,
  TextInput,
  Select,
} from "flowbite-react";
import HeaderBar from "./components/Header";
import ToastMSG, { ToastErrMSG } from "./components/Toast";

function AddActivityComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { action, id } = location.state || { action: "nuevo", id: null };
  const isEdit = action === "editar";
  const [activityId, setActivityId] = useState("");

  const [calendars, setCalendars] = useState([]);
  const [zones, setZones] = useState([]);
  const [activities, setActivities] = useState([]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [img, setImg] = useState("");
  const [paymentNeed, setPaymentNeed] = useState(false);
  const [price, setPrice] = useState("");
  const [blocked, setBlocked] = useState(false);

  const [calendar, setCalendar] = useState("");
  const [zone, setZone] = useState("");

  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");

  // Cargar datos
  useEffect(() => {
    const fetchData = async () => {
      const calSnapshot = await getDocs(collection(db, "Calendars"));
      setCalendars(
        calSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      );

      const qActivities = query(
        collection(db, "Activities"),
        where("Blocked", "==", false),
      );
      const actSnapshot = await getDocs(qActivities);
      const actList = actSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setActivities(actList);

      const zoneSnapshot = await getDocs(collection(db, "Zones"));
      setZones(zoneSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      if (isEdit && id) {
        const actDoc = actList.find((a) => a.id === id);
        if (actDoc) {
          setActivityId(actDoc.id);
          setName(actDoc.Name);
          setDescription(actDoc.Description);
          setDuration(actDoc.Duration);
          setImg(actDoc.IMG);
          setPaymentNeed(actDoc.PaymentNeed);
          setPrice(actDoc.Price);
          setBlocked(actDoc.Blocked ?? false);

          const activityRef = doc(db, "Activities", actDoc.id);
          const q = query(
            collection(db, "ActivitySchedule"),
            where("Activity", "==", activityRef),
          );
          const scheduleSnapshot = await getDocs(q);

          if (!scheduleSnapshot.empty) {
            const scheduleData = scheduleSnapshot.docs[0].data();
            setCalendar(scheduleData.Calendar || null);
            setZone(scheduleData.Zone || null);
          }
        }
      }
    };

    fetchData();
  }, [isEdit, id]);

  const clearForm = () => {
    setName("");
    setDescription("");
    setDuration("");
    setImg("");
    setPaymentNeed(false);
    setPrice("");
    setCalendar("");
    setZone("");
    setBlocked(false);
  };

  const saveActivity = async () => {
    if (
      !name ||
      !description ||
      !duration ||
      !img ||
      price === undefined ||
      !calendar ||
      !zone
    ) {
      setMessage("ERROR: Complete todos los campos");
      setStatus("failure");
      return;
    }

    const existingActivity = activities.find(
      (a) =>
        a.Name.toLowerCase() === name.toLowerCase() &&
        a.id !== activityId &&
        !a.Blocked,
    );
    if (existingActivity) {
      setMessage("ERROR: Ya existe una actividad con ese nombre");
      setStatus("failure");
      return;
    }

    try {
      const finalPrice = parseFloat(price).toFixed(2);
      let activityRef;

      if (isEdit && activityId) {
        activityRef = doc(db, "Activities", activityId);
        await updateDoc(activityRef, {
          Name: name,
          Description: description,
          Duration: duration,
          IMG: img,
          PaymentNeed: paymentNeed,
          Price: finalPrice,
          Blocked: false,
        });

        const q = query(
          collection(db, "ActivitySchedule"),
          where("Activity", "==", activityRef),
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          await updateDoc(snapshot.docs[0].ref, {
            Calendar: calendar,
            Zone: zone,
          });
        }

        setMessage("Actividad actualizada correctamente");
      } else {
        activityRef = await addDoc(collection(db, "Activities"), {
          Name: name,
          Description: description,
          Duration: duration,
          IMG: img,
          PaymentNeed: paymentNeed,
          Price: finalPrice,
          Blocked: false,
        });

        await addDoc(collection(db, "ActivitySchedule"), {
          Activity: activityRef,
          Calendar: calendar,
          Zone: zone,
          StartDate: new Date(new Date().setHours(0, 0, 0, 0)),
          EndDate: new Date(new Date().getFullYear(), 11, 31),
        });

        setMessage("Actividad creada correctamente");
      }

      setStatus("success");
      clearForm();

      navigate("/main", {
        state: {
          message: isEdit
            ? "Actividad actualizada correctamente"
            : "Actividad creada correctamente",
          err: "success",
        },
      });
    } catch (error) {
      console.error(error);
      setMessage("ERROR al guardar la actividad");
      setStatus("failure");
    }
  };

  const handleNameChange = async (value) => {
    setName(value);
    if (isEdit) {
      if (!value) {
        setName("");
      } else {
        const activity = activities.find((a) => a.Name === value);
        if (activity && activity.id !== id) {
          setActivityId(activity.id);
          setDescription(activity.Description || "");
          setDuration(activity.Duration || "");
          setImg(activity.IMG || "");
          setPaymentNeed(activity.PaymentNeed || false);
          setPrice(activity.Price || "");

          try {
            const activityRef = doc(db, "Activities", activity.id);
            const q = query(
              collection(db, "ActivitySchedule"),
              where("Activity", "==", activityRef),
            );
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
              const scheduleData = snapshot.docs[0].data();
              setCalendar(scheduleData.Calendar || null);
              setZone(scheduleData.Zone || null);
            } else {
              setCalendar(null);
              setZone(null);
            }
          } catch (error) {
            console.error("Error cargando calendario/zona:", error);
            setCalendar(null);
            setZone(null);
          }
        }
      }
    } else {
      setDescription("");
      setDuration("");
      setImg("");
      setPaymentNeed(false);
      setPrice("");
      setCalendar(null);
      setZone(null);
    }
  };

  if (calendars.length === 0 || zones.length === 0) return null;

  return (
    <section>
      <HeaderBar />
      <div className="flex justify-center mt-6">
        <Card className="mediumSize">
          <h5 className="text-2xl font-bold mb-4">
            {isEdit ? "Editar Actividad" : "Nueva Actividad"}
          </h5>
          <form>
            <div className="mb-4">
              <Label htmlFor="activityName" value="Nombre" />
              <TextInput
                id="activityName"
                placeholder="Nombre de la Actividad"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                list={isEdit ? "activitiesList" : undefined}
              />
              {isEdit && (
                <datalist id="activitiesList">
                  {activities.map((a) => (
                    <option key={a.id} value={a.Name} />
                  ))}
                </datalist>
              )}
            </div>

            <div className="mb-4">
              <Label htmlFor="description" value="Descripción" />
              <TextInput
                id="description"
                placeholder="Descripción de la Actividad"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="duration" value="Duración (minutos)" />
              <TextInput
                id="duration"
                placeholder="Duración"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || "")}
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="img" value="Imagen" />
              <TextInput
                id="img"
                placeholder="URL de la Imagen"
                value={img}
                onChange={(e) => setImg(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="calendarSelect" value="Calendario" />
              <Select
                id="calendarSelect"
                value={calendar?.CalendarID || ""}
                onChange={(e) => {
                  const selected = calendars.find(
                    (c) => c.CalendarID === e.target.value,
                  );
                  setCalendar(selected || null);
                }}
              >
                <option value="">Seleccione un Calendario</option>
                {calendars.map((c) => (
                  <option key={c.CalendarID} value={c.CalendarID}>
                    {c.CalendarID}
                  </option>
                ))}
              </Select>
            </div>

            <div className="mb-4">
              <Label htmlFor="zoneSelect" value="Zona" />
              <Select
                id="zoneSelect"
                value={zone?.Name || ""}
                onChange={(e) => {
                  const selectedZone = zones.find(
                    (z) => z.Name === e.target.value,
                  );
                  setZone(selectedZone || null);
                }}
              >
                <option value="">Seleccione una Zona</option>
                {zones.map((z) => (
                  <option key={z.Name} value={z.Name}>
                    {z.Name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="mb-4">
              <Label htmlFor="price" value="Precio" />
              <TextInput
                id="price"
                placeholder="Precio"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>

            <div className="mb-4 flex items-center gap-2">
              <Checkbox
                id="paymentNeed"
                checked={paymentNeed}
                onChange={(e) => setPaymentNeed(e.target.checked)}
              />
              <Label htmlFor="paymentNeed">Pago necesario</Label>
            </div>

            <div className="flex gap-4 mt-4">
              <Button onClick={saveActivity}>
                {isEdit ? "Guardar Cambios" : "Crear Actividad"}
              </Button>
            </div>
          </form>

          <div className="mt-4">
            {status === "success" && <ToastMSG message={message} />}
            {status === "failure" && <ToastErrMSG message={message} />}
          </div>
        </Card>
      </div>
    </section>
  );
}

export default function AddActivity() {
  return sessionStorage.getItem("user") ? (
    <AddActivityComponent />
  ) : (
    <Navigate replace to="/" />
  );
}
